/* ============================================================
   Redesign predictive search (vanilla JS, no app dependency).
   Powered by Shopify's native /search/suggest.json.

   Owns: the header search overlay — open/close, debounced live
   results, keyboard navigation, and submit-through to the full
   /search results page (sections/redesign-search.liquid).

   Add-ons (product type, or the exact tag "addon") are hidden
   unless tagged "standalone". Matching collections are listed
   above the products.
   ============================================================ */
(function () {
  'use strict';

  var panel = document.querySelector('[data-rds-searchpanel]');
  if (!panel) return;

  var openers = Array.prototype.slice.call(document.querySelectorAll('[data-rds-search-open]'));
  var closeBtn = panel.querySelector('[data-rds-search-close]');
  var input = panel.querySelector('[data-rds-search-input]');
  var results = panel.querySelector('[data-rds-search-results]');
  var form = panel.querySelector('form');

  var MONEY = panel.getAttribute('data-money-format') || 'RM {{amount}}';
  var LIMIT = 6;          // products shown
  var COLL_LIMIT = 3;     // collections shown
  var FETCH_LIMIT = 10;   // ask for more so hidden add-ons don't leave gaps
  var HIDDEN_COLLECTIONS = ['add-ons', 'cards', 'hidden', 'all', 'frontpage'];

  var timer = null;
  var lastQuery = '';
  var activeIndex = -1;
  var controller = null;

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, MONEY); } catch (e) { /* fall through */ }
    }
    var v = cents / 100;
    return 'RM ' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }

  /* suggest.json returns prices as major-unit strings ("88.00"), not cents */
  function toCents(v) {
    if (typeof v === 'number') return v;
    var n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* highlight the matched run inside a title */
  function mark(title, q) {
    var t = String(title || '');
    var i = t.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1 || !q) return esc(t);
    return esc(t.slice(0, i)) + '<mark>' + esc(t.slice(i, i + q.length)) + '</mark>' + esc(t.slice(i + q.length));
  }

  /* add-ons that aren't sold on their own stay out of search */
  function isAddon(p) {
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.indexOf('standalone') > -1) return false;
    var type = String(p.type || '').toLowerCase();
    if (type.indexOf('addon') > -1 || type.indexOf('greeting') > -1) return true;
    // exact 'addon' only: picker tags like 'tag:plushaddon' also sit on standalone gifts
    return tags.indexOf('addon') > -1;
  }

  function searchUrl(q) {
    return '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last';
  }

  function thumbOf(img, size) {
    return img ? img.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_' + size + '$1$2') : '';
  }

  function open() {
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rds-search-locked');
    openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
    setTimeout(function () { if (input) input.focus(); }, 40);
  }

  function close() {
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rds-search-locked');
    openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
    activeIndex = -1;
  }

  function setState(html) {
    if (results) results.innerHTML = html;
    activeIndex = -1;
  }

  function renderIdle() {
    var chips = (panel.getAttribute('data-popular') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!chips.length) { setState(''); return; }
    setState(
      '<div class="rds-sp__label">Popular searches</div>' +
      '<div class="rds-sp__chips">' +
      chips.map(function (c) {
        return '<a class="rds-pill" href="' + esc(searchUrl(c)) + '">' + esc(c) + '</a>';
      }).join('') +
      '</div>'
    );
  }

  function renderEmpty(q) {
    setState(
      '<div class="rds-sp__empty">' +
        '<p>No matches for <b>' + esc(q) + '</b></p>' +
        '<span>Try a shorter word: &ldquo;rose&rdquo; rather than &ldquo;red roses bunch&rdquo;.</span>' +
      '</div>'
    );
  }

  function renderResults(products, collections, q) {
    products = products.filter(function (p) { return !isAddon(p); }).slice(0, LIMIT);
    collections = collections.filter(function (c) {
      return HIDDEN_COLLECTIONS.indexOf(String(c.handle || '').toLowerCase()) === -1;
    }).slice(0, COLL_LIMIT);

    if (!products.length && !collections.length) { renderEmpty(q); return; }

    var html = '';

    if (collections.length) {
      html += '<div class="rds-sp__label">Collections</div>' +
        '<div class="rds-sp__rows" role="listbox">' +
        collections.map(function (c) {
          var thumb = thumbOf((c.featured_image && c.featured_image.url) || '', '120x120');
          return '' +
            '<a class="rds-sp__row" href="' + esc(c.url) + '" role="option">' +
              '<span class="rds-sp__thumb">' + (thumb ? '<img src="' + esc(thumb) + '" alt="" loading="lazy">' : '') + '</span>' +
              '<span class="rds-sp__meta">' +
                '<span class="rds-sp__name">' + mark(c.title, q) + '</span>' +
                '<span class="rds-sp__price">Collection</span>' +
              '</span>' +
            '</a>';
        }).join('') +
        '</div>';
    }

    if (products.length) {
      html += '<div class="rds-sp__label"' + (collections.length ? ' style="margin-top:18px"' : '') + '>Products</div>' +
        '<div class="rds-sp__rows" role="listbox">' +
        products.map(function (p) {
          var img = (p.featured_image && p.featured_image.url) || p.image || '';
          var varies = p.price_min != null && p.price_max != null && p.price_min !== p.price_max;
          var cents = toCents(varies ? p.price_min : p.price);
          var priceHtml = (varies ? '<span class="from">from</span> ' : '') + esc(money(cents));
          var thumb = thumbOf(img, '120x120');

          return '' +
            '<a class="rds-sp__row" href="' + esc(p.url) + '" role="option">' +
              '<span class="rds-sp__thumb">' + (thumb ? '<img src="' + esc(thumb) + '" alt="" loading="lazy">' : '') + '</span>' +
              '<span class="rds-sp__meta">' +
                '<span class="rds-sp__name">' + mark(p.title, q) + '</span>' +
                '<span class="rds-sp__price">' + priceHtml + (p.available === false ? ' · <i>Sold out</i>' : '') + '</span>' +
              '</span>' +
            '</a>';
        }).join('') +
        '</div>';
    }

    html += '<a class="rds-sp__all" href="' + esc(searchUrl(q)) + '">See all results for &ldquo;' + esc(q) + '&rdquo;</a>';
    setState(html);
  }

  function fetchResults(q) {
    if (controller) { try { controller.abort(); } catch (e) {} }
    controller = ('AbortController' in window) ? new AbortController() : null;

    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
              '&resources[type]=product,collection&resources[limit]=' + FETCH_LIMIT +
              '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor';

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (q !== lastQuery) return; // a newer keystroke already won
        var res = (data && data.resources && data.resources.results) || {};
        renderResults(res.products || [], res.collections || [], q);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (q !== lastQuery) return;
        setState('<div class="rds-sp__empty"><p>Search is unavailable right now</p><span>Please try again in a moment.</span></div>');
      });
  }

  function onType() {
    var q = (input.value || '').trim();
    lastQuery = q;
    clearTimeout(timer);

    if (q.length < 2) { renderIdle(); return; }
    setState('<div class="rds-sp__loading"><span></span><span></span><span></span></div>');
    timer = setTimeout(function () { fetchResults(q); }, 220);
  }

  /* ---- keyboard navigation over the rendered rows ---- */
  function rows() {
    return Array.prototype.slice.call(panel.querySelectorAll('.rds-sp__row, .rds-sp__all'));
  }

  function move(delta) {
    var list = rows();
    if (!list.length) return;
    list.forEach(function (r) { r.classList.remove('is-active'); });
    activeIndex += delta;
    if (activeIndex < 0) activeIndex = list.length - 1;
    if (activeIndex >= list.length) activeIndex = 0;
    var el = list[activeIndex];
    el.classList.add('is-active');
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  /* ---- wiring ---- */
  openers.forEach(function (o) {
    o.addEventListener('click', function (e) {
      e.preventDefault();
      open();
      renderIdle();
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', function (e) { e.preventDefault(); close(); });

  // click the dim backdrop (but not the sheet) to dismiss
  panel.addEventListener('click', function (e) {
    if (e.target === panel) close();
  });

  if (input) {
    input.addEventListener('input', onType);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter') {
        var list = rows();
        e.preventDefault();
        if (activeIndex > -1 && list[activeIndex]) { window.location.href = list[activeIndex].href; return; }
        // The legacy theme script (app.js) blocks Enter on every input site-wide,
        // so the form never submits on its own. Go to the results page ourselves.
        var q = (input.value || '').trim();
        if (q) window.location.href = searchUrl(q);
      }
    });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      var q = (input.value || '').trim();
      if (!q) { e.preventDefault(); }
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
    // "/" anywhere opens search, as long as the user isn't already typing
    var tag = (document.activeElement && document.activeElement.tagName) || '';
    if (e.key === '/' && !panel.classList.contains('is-open') &&
        tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' &&
        !(document.activeElement && document.activeElement.isContentEditable)) {
      e.preventDefault();
      open();
      renderIdle();
    }
  });
})();
